import { useEffect, useMemo, useRef, useState } from 'react';
import { zonedNow } from '../../lib/tz.ts';
import { scheduleWindowSummary } from '../../lib/schedule-summary.ts';
import { CheckMark, WarningMark } from '../../components/StatusMarks.tsx';
import {
  buildWeek,
  playlistColor,
  minutesToTime,
  DAY_LABELS,
  MINUTES_PER_DAY,
  type CalendarSource,
  type PlacedBlock,
} from './calendar.ts';

const HOUR_PX = 44; // row height per hour; 24h grid = 1056px tall
const GRID_PX = HOUR_PX * 24;
const DAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export interface WeekCalendarProps {
  sources: CalendarSource[];
  tz: string;
  tzNote?: string | null;
  /** Live-resolver reconciliation for the current hour: 'match' | 'differ' | null. */
  reconcile?: 'match' | 'differ' | null;
  /** Viewers can look but not create/edit — hides slot buttons + edit affordance. */
  readOnly?: boolean;
  onEditSchedule: (scheduleId: string) => void;
  onCreateSlot: (day: number, startMin: number, endMin: number) => void;
}

interface Tip {
  text: string;
  sub: string;
  x: number;
  y: number;
}

export function WeekCalendar({
  sources,
  tz,
  tzNote,
  reconcile,
  readOnly = false,
  onEditSchedule,
  onCreateSlot,
}: WeekCalendarProps) {
  const week = useMemo(() => buildWeek(sources), [sources]);

  // Recompute "now" each minute so the indicator + today highlight stay live.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const now = useMemo(() => zonedNow(tz, nowMs), [tz, nowMs]);

  const [tip, setTip] = useState<Tip | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Legend: each playlist present this week, with its consistent color.
  const legend = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of sources) if (!seen.has(s.playlist_id)) seen.set(s.playlist_id, s.playlist_name);
    return [...seen.entries()].map(([id, name]) => ({ id, name, color: playlistColor(id) }));
  }, [sources]);

  // Scroll the morning into view on first render (business hours, not midnight).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_PX;
  }, []);

  const hours = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="weekcal card">
      <div className="weekcal-toolbar">
        <div className="weekcal-legend" aria-label="Playlists">
          {legend.map((p) => (
            <span key={p.id} className="wc-legend-item">
              <span className="wc-swatch" style={{ background: p.color }} aria-hidden />
              {p.name}
            </span>
          ))}
          <span className="wc-legend-item" title="Slots no schedule covers fall back to the org default">
            <span className="wc-swatch fallback" aria-hidden />
            Org default
          </span>
          {legend.length === 0 && <span className="text-dim">No schedules cover this target.</span>}
        </div>
        <div className="weekcal-meta">
          {reconcile && (
            <span className={`wc-reconcile ${reconcile}`} title="Compared against the live resolver preview for the current hour">
              <span className="wc-reconcile-mark" aria-hidden>
                {reconcile === 'match' ? <CheckMark /> : <WarningMark />}
              </span>{' '}
              {reconcile === 'match' ? 'Matches live resolver' : 'Differs from live resolver'}
            </span>
          )}
          <span className="wc-tz" title="Times are shown in this timezone">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5V12l3 2" />
            </svg>
            {tz}
          </span>
        </div>
      </div>
      {tzNote && <div className="weekcal-tznote">{tzNote}</div>}

      {/* Mobile-only affordance — the grid scrolls sideways below 760px. */}
      <div className="weekcal-hint">Scroll sideways to see the full week →</div>

      <div className="weekcal-hscroll">
        <div className="weekcal-head">
          <div className="wc-corner" />
          {DAY_LABELS.map((d, i) => (
            <div key={d} className={`wc-day-head${i === now.weekday ? ' today' : ''}`}>
              <span className="wc-day-name">{d}</span>
              {i === now.weekday && <span className="wc-today-badge">Today</span>}
            </div>
          ))}
        </div>

        <div className="weekcal-scroll" ref={scrollRef}>
        <div className="weekcal-grid" style={{ height: GRID_PX }}>
          <div className="wc-hours" aria-hidden>
            {hours.map((h) => (
              <div key={h} className="wc-hour-label" style={{ height: HOUR_PX }}>
                {h === 0 ? '' : minutesToTime(h * 60)}
              </div>
            ))}
          </div>

          {DAY_LABELS.map((dName, day) => (
            <div key={dName} className={`wc-col${day === now.weekday ? ' today' : ''}`}>
              {/* Hour grid lines + clickable empty slots (keyboard accessible). */}
              {hours.map((h) =>
                readOnly ? (
                  <div key={h} className="wc-slot readonly" style={{ height: HOUR_PX }} aria-hidden />
                ) : (
                  <button
                    key={h}
                    type="button"
                    className="wc-slot"
                    style={{ height: HOUR_PX }}
                    aria-label={`New schedule ${DAY_FULL[day]} ${minutesToTime(h * 60)}`}
                    onClick={() => onCreateSlot(day, h * 60, Math.min((h + 1) * 60, MINUTES_PER_DAY))}
                  />
                ),
              )}

              {/* Schedule blocks. */}
              {week[day]!.map((b) => (
                <BlockView
                  key={b.key}
                  block={b}
                  readOnly={readOnly}
                  onEdit={onEditSchedule}
                  onTip={(text, sub, x, y) => setTip({ text, sub, x, y })}
                  onTipEnd={() => setTip(null)}
                />
              ))}

              {/* Current-time indicator on today's column. */}
              {day === now.weekday && (
                <div
                  className="wc-now"
                  style={{ top: (now.minutes / MINUTES_PER_DAY) * GRID_PX }}
                  aria-label={`Now: ${minutesToTime(now.minutes)}`}
                >
                  <span className="wc-now-dot" />
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      </div>

      {tip && (
        <div className="wc-tooltip" role="tooltip" style={{ left: tip.x, top: tip.y }}>
          <strong>{tip.text}</strong>
          <span>{tip.sub}</span>
        </div>
      )}
    </div>
  );
}

function BlockView({
  block,
  readOnly,
  onEdit,
  onTip,
  onTipEnd,
}: {
  block: PlacedBlock;
  readOnly: boolean;
  onEdit: (id: string) => void;
  onTip: (text: string, sub: string, x: number, y: number) => void;
  onTipEnd: () => void;
}) {
  const color = playlistColor(block.playlistId);
  const top = (block.startMin / MINUTES_PER_DAY) * GRID_PX;
  const height = ((block.endMin - block.startMin) / MINUTES_PER_DAY) * GRID_PX;
  const laneW = 100 / block.laneCount;
  const range = `${minutesToTime(block.startMin)}–${minutesToTime(block.endMin)}`;
  const summary = scheduleWindowSummary({
    days_mask: 0b1111111, // day is implied by the column; describe the time window
    start_time: block.continuesFromPrevDay ? null : minutesToTime(block.startMin),
    end_time: block.continuesToNextDay ? null : minutesToTime(block.endMin),
    start_date: null,
    end_date: null,
  });
  const statusText =
    block.status === 'win'
      ? 'Plays here'
      : block.status === 'lose'
        ? 'Overridden by a higher-priority schedule'
        : 'Partly overridden';
  const aria = `${block.playlistName}, ${range}, priority ${block.priority}. ${statusText}.${readOnly ? '' : ' Edit.'}`;

  const showTip = (e: React.MouseEvent | React.FocusEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    onTip(`${block.playlistName} · ${range}`, `${statusText}. ${summary}`.trim(), rect.left, rect.bottom + 6);
  };

  return (
    <button
      type="button"
      className={`wc-block ${block.status}`}
      style={{
        top,
        height: Math.max(height, 15),
        left: `calc(${block.lane * laneW}% + 2px)`,
        width: `calc(${laneW}% - 4px)`,
        // Custom props consumed by the CSS for fills/borders.
        ['--wc-color' as string]: color,
      }}
      aria-label={aria}
      disabled={readOnly}
      onClick={() => onEdit(block.scheduleId)}
      onMouseEnter={showTip}
      onFocus={showTip}
      onMouseLeave={onTipEnd}
      onBlur={onTipEnd}
    >
      {block.continuesFromPrevDay && <span className="wc-cont top" aria-hidden />}
      <span className="wc-block-body">
        <span className="wc-block-meta">
          <span className="wc-block-name">{block.playlistName}</span>
          <span className="wc-prio" aria-hidden>
            P{block.priority}
          </span>
        </span>
        {height > 40 && <span className="wc-block-time">{range}</span>}
      </span>
      {/* Precise "overridden here" hatching over just the sub-ranges a higher-ranked
          schedule wins — the solid parts are where this block actually plays. */}
      {block.overriddenRanges.map(([s, e], i) => {
        const span = block.endMin - block.startMin;
        return (
          <span
            key={i}
            className="wc-eclipse"
            aria-hidden
            style={{
              top: `${((s - block.startMin) / span) * 100}%`,
              height: `${((e - s) / span) * 100}%`,
            }}
          />
        );
      })}
      {block.continuesToNextDay && <span className="wc-cont bottom" aria-hidden />}
    </button>
  );
}
